// @file Global snackbar store
import { trackEvent } from '@@/bits/analytics'
import window from '@@/bits/global'
import { __ } from '@@/bits/intl'
import { SnackbarLoadingStatus, SnackbarNotificationType } from '@@/enums'
import { AppNotificationItemType } from '@@/native_bridge/actions'
import { useAccessibilitySettingsStore } from '@@/pinia/accessibility_settings_store'
import type { SnackbarLoadingState } from '@@/types'
import { isEqual, uniqueId } from 'es-toolkit/compat'
import { defineStore } from 'pinia'
import { AllowedComponentProps, Component, markRaw, ref, VNodeProps } from 'vue'

type GlobalSnackbarActionContent = Component
type GlobalSnackbarActionContentProps = Record<string, unknown> & VNodeProps & AllowedComponentProps

interface GlobalSnackbarNotification {
  notificationType?: SnackbarNotificationType
  message: string
  timeout?: number
  persist?: boolean
  dismissable?: boolean
  iconName?: string
  actionText?: string
  actionTextActions?: ((event?: Event, uid?: string) => void)[]
  // Untranslated grouping key for analytics. The event's `label` is the message, which is
  // `__()`-localized and so splits one snackbar into one warehouse row per locale. Set this on
  // any snackbar whose rate is meant to be read.
  analyticsId?: string
  shouldTruncate?: boolean
  loadingState?: SnackbarLoadingState
  actionContentSlot?: GlobalSnackbarActionContent
  actionContentSlotProps?: GlobalSnackbarActionContentProps
}

function toNonReactiveActionContent(
  slot: GlobalSnackbarActionContent | undefined,
): GlobalSnackbarActionContent | undefined {
  if (slot == null) return undefined
  return typeof slot === 'string' ? slot : markRaw(slot)
}

// Internal type
interface QueuedGlobalSnackbarNotification extends GlobalSnackbarNotification {
  uid: string
  // The snackbar will occupy the originalIndex-th spot on the screen.
  // Since snackbars may be removed, a particular snackbar's position within snackbarArray might change.
  // In order to ensure snackbars don't shift around and don't overlap,
  // we assign a fixed originalIndex when it's originally/first added to the array.
  originalIndex: number
  timeoutId?: number
}

interface SnackbarConfig {
  allowDuplicate?: boolean
}

const DEFAULT_TIMEOUT = 3000

const snackbarNotificationTypePresets: Record<
  SnackbarNotificationType,
  { [P in keyof QueuedGlobalSnackbarNotification]?: QueuedGlobalSnackbarNotification[P] }
> = {
  [SnackbarNotificationType.error]: {
    iconName: 'exclamation',
  },
  [SnackbarNotificationType.success]: {
    iconName: 'checkmark',
  },
  [SnackbarNotificationType.spinner]: {
    loadingState: {
      status: SnackbarLoadingStatus.loading,
      progress: 0,
    },
  },
  [SnackbarNotificationType.warning]: {
    iconName: 'exclamation',
  },
}

function trackSnackbarEvent(
  action: 'Shown' | 'Action button clicked' | 'Dismissed' | 'Auto-dismissed',
  snackbar: Pick<GlobalSnackbarNotification, 'message' | 'notificationType' | 'actionText' | 'analyticsId'>,
): void {
  trackEvent('Snackbar', action, snackbar.message, null, {
    notificationType: snackbar.notificationType,
    actionText: snackbar.actionText,
    analyticsId: snackbar.analyticsId,
  })
}

const useGlobalSnackbarStore = defineStore('globalSnackbar', () => {
  const snackbarArray = ref([] as QueuedGlobalSnackbarNotification[])

  function shouldPersistSnackbar(snackbar: GlobalSnackbarNotification): boolean {
    const userAccessibilityInAppNotificationsPersisted = !useAccessibilitySettingsStore().isAppMessagesAutoDismissed
    return userAccessibilityInAppNotificationsPersisted || snackbar.persist === true
  }

  function scheduleAutoDismissForSnackbar(snackbar: Pick<QueuedGlobalSnackbarNotification, 'uid' | 'timeout'>): void {
    const target = snackbarArray.value.find((s) => s.uid === snackbar.uid)
    if (target == null) return

    target.timeoutId = window.setTimeout(() => {
      const liveSnackbar = snackbarArray.value.find((s) => s.uid === snackbar.uid)
      if (liveSnackbar != null) {
        trackSnackbarEvent('Auto-dismissed', liveSnackbar)
        snackbarArray.value = snackbarArray.value.filter((s) => s.uid !== snackbar.uid)
      }
    }, snackbar.timeout ?? DEFAULT_TIMEOUT)
  }

  function setSnackbar(newSnackbar: GlobalSnackbarNotification, config?: SnackbarConfig): string {
    const uid = uniqueId('snack_')

    const originalIndex =
      snackbarArray.value.length === 0
        ? 0
        : Math.max(...snackbarArray.value.map((snackbar) => snackbar.originalIndex)) + 1

    const presetAttributes =
      newSnackbar.notificationType == null ? null : snackbarNotificationTypePresets[newSnackbar.notificationType]

    const newSnackbarNotification: QueuedGlobalSnackbarNotification = {
      ...newSnackbar,
      actionContentSlot: toNonReactiveActionContent(newSnackbar.actionContentSlot),
      uid,
      originalIndex,
      ...presetAttributes,
      dismissable: newSnackbar.dismissable ?? presetAttributes?.dismissable ?? true,
    }

    if (config?.allowDuplicate === false) {
      // If the snackbar is not allowed to be duplicated,
      // we need to check if the snackbar is already in the array
      // And just return the uid of the existing snackbar if it exists
      const existingSnackbar = snackbarArray.value.find((snackbar) => {
        // Compare the snackbar without the uid and originalIndex
        const { uid: _uid1, originalIndex: _index1, timeoutId: _timeoutId1, ...snackbarWithoutId } = snackbar
        const {
          uid: _uid2,
          originalIndex: _index2,
          timeoutId: _timeoutId2,
          ...newSnackbarWithoutId
        } = newSnackbarNotification
        return isEqual(snackbarWithoutId, newSnackbarWithoutId)
      })

      if (existingSnackbar != null) {
        // Refresh the timeout for the existing snackbar
        if (existingSnackbar.timeoutId != null) {
          window.clearTimeout(existingSnackbar.timeoutId)
          existingSnackbar.timeoutId = window.setTimeout(() => {
            snackbarArray.value = snackbarArray.value.filter((snackbar) => snackbar.uid !== existingSnackbar.uid)
          }, newSnackbarNotification.timeout ?? DEFAULT_TIMEOUT)
        }
        return existingSnackbar.uid
      }
    }

    snackbarArray.value = [...snackbarArray.value, newSnackbarNotification]

    // Only snackbars that offer an action, because this exists to give "Action button clicked" a
    // denominator — an informational snackbar has no click to divide. Emitting for every snackbar
    // would multiply RudderStack volume across the whole app for numbers nobody reads.
    if (newSnackbar.actionText != null) {
      trackSnackbarEvent('Shown', newSnackbar)
    }

    // if user prefers to persist snackbars, return immediately
    if (shouldPersistSnackbar(newSnackbar)) {
      return uid
    }

    scheduleAutoDismissForSnackbar(newSnackbarNotification)

    return uid
  }

  // Caller must perform the intended action first; this shows a success snackbar with an Undo button.
  // The snackbar auto-dismisses the moment undo is taken, so an in-flight async `undoAction` can't be triggered twice.
  function setSnackbarWithUndo({
    message,
    undoAction,
    timeout,
    undoText,
    notificationType,
    actionContentSlot,
    actionContentSlotProps,
  }: {
    message: string
    undoAction: (event?: Event) => void | Promise<void>
    timeout?: number
    undoText?: string
    notificationType?: SnackbarNotificationType
    actionContentSlot?: GlobalSnackbarActionContent
    actionContentSlotProps?: GlobalSnackbarActionContentProps
  }): string {
    let uid = ''
    uid = setSnackbar({
      message,
      notificationType: notificationType ?? SnackbarNotificationType.success,
      timeout,
      actionText: undoText ?? __('Undo'),
      actionTextActions: [
        async (event?: Event) => {
          removeSnackbar(uid)
          await undoAction(event)
        },
      ],
      actionContentSlot,
      actionContentSlotProps,
    })
    return uid
  }

  function updateSnackbar(snackbarToUpdateUid: string, newSnackbar: GlobalSnackbarNotification): void {
    const currentSnackbar = snackbarArray.value.find((snackbar) => snackbar.uid === snackbarToUpdateUid)
    if (currentSnackbar == null) return

    // Snackbar timeout logic
    // If current snackbar has a timeouId, clear it. Else do nothing.
    // If the new snackbar is not persistent, set a new timeout.
    if (currentSnackbar.timeoutId != null) {
      window.clearTimeout(currentSnackbar.timeoutId)
    }

    if (!shouldPersistSnackbar(newSnackbar)) {
      scheduleAutoDismissForSnackbar({ uid: snackbarToUpdateUid, timeout: newSnackbar.timeout })
    }

    snackbarArray.value = snackbarArray.value.map((snackbar) =>
      snackbar.uid === snackbarToUpdateUid
        ? {
            ...currentSnackbar,
            ...newSnackbar,
            actionContentSlot: toNonReactiveActionContent(newSnackbar.actionContentSlot),
          }
        : snackbar,
    )
  }

  function removeSnackbar(snackbarToRemoveUid: string): void {
    const snackbar = snackbarArray.value.find((s) => s.uid === snackbarToRemoveUid)
    if (snackbar?.timeoutId != null) {
      window.clearTimeout(snackbar.timeoutId)
    }
    snackbarArray.value = snackbarArray.value.filter((s) => s.uid !== snackbarToRemoveUid)
  }

  function executeActions(actions: ((event?: Event, uid?: string) => void)[] = [], event: Event, uid: string): void {
    const snackbar = snackbarArray.value.find((s) => s.uid === uid)
    if (snackbar != null) {
      trackSnackbarEvent('Action button clicked', snackbar)
    }
    actions.forEach((fn): void => {
      if (typeof fn === 'function') {
        fn(event, uid)
      }
    })
  }

  function removeAllSnackbars(): void {
    snackbarArray.value.forEach((snackbar) => {
      if (snackbar.timeoutId != null) {
        window.clearTimeout(snackbar.timeoutId)
      }
    })
    snackbarArray.value = []
  }

  function genericFetchError(): void {
    setSnackbar({
      message: __('Oops! Something went wrong. Please try again.'),
      notificationType: SnackbarNotificationType.error,
    })
  }

  function isMessageBeingShown(message): boolean {
    return snackbarArray.value.some((snackbar) => snackbar.message === message)
  }

  function isSnackbarBeingShown(snackbarUid: string): boolean {
    return snackbarArray.value.some((snackbar) => snackbar.uid === snackbarUid)
  }

  return {
    snackbarArray,
    setSnackbar,
    setSnackbarWithUndo,
    updateSnackbar,
    removeSnackbar,
    removeAllSnackbars,
    executeActions,
    genericFetchError,
    isMessageBeingShown,
    isSnackbarBeingShown,
  }
})

/**
 * Maps a GlobalSnackbarNotification object to showAppNotification parameters.
 * This is used for displaying notifications in the native app.
 */
function mapSnackbarToAppNotification(notification: GlobalSnackbarNotification): {
  notification: AppNotificationItemType
} {
  const appNotification: {
    text: string
    icon?: string
    type?: 'default' | 'error'
    button?: {
      title: string
      onPress(): void
      accessibilityLabel: string
    }
  } = {
    text: notification.message,
  }

  // Map notification type to app notification type
  if (notification.notificationType === SnackbarNotificationType.error) {
    appNotification.type = 'error'
    appNotification.icon = 'exclamation'
  } else if (notification.notificationType === SnackbarNotificationType.success) {
    appNotification.icon = 'checkmark'
  }

  // Map icon if explicitly provided (overrides type-based icon)
  if (notification.iconName != null) {
    appNotification.icon = notification.iconName
  }

  // Map action text and actions to button
  if (notification.actionText != null && notification.actionTextActions != null) {
    appNotification.button = {
      title: notification.actionText,
      onPress: () => {
        notification.actionTextActions?.forEach((fn) => {
          if (typeof fn === 'function') {
            fn()
          }
        })
      },
      accessibilityLabel: notification.actionText,
    }
  }

  return { notification: appNotification }
}

export { mapSnackbarToAppNotification, SnackbarNotificationType, trackSnackbarEvent, useGlobalSnackbarStore }
export type {
  GlobalSnackbarActionContent,
  GlobalSnackbarActionContentProps,
  GlobalSnackbarNotification,
  QueuedGlobalSnackbarNotification,
}
